import { useEffect, useState, useCallback, useMemo } from "react";
import axios from "axios";
import { toast } from "sonner";
import { API, useAuth } from "../App";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Badge } from "../components/ui/badge";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "../components/ui/tabs";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "../components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "../components/ui/table";
import { Skeleton } from "../components/ui/skeleton";
import {
  Shield, Activity, Users, AlertTriangle, Search, Filter, Download, FileText,
  CheckCircle2, XCircle, Eye, Info, Clock, RefreshCw, LockKeyhole, Server, MapPin,
  ShieldCheck, ShieldAlert, TrendingUp, BadgeCheck, Trash2,
} from "lucide-react";
import {
  BarChart, Bar, LineChart, Line, PieChart, Pie, Cell, XAxis, YAxis, Tooltip, ResponsiveContainer, Legend, CartesianGrid,
} from "recharts";

const SEVERITY_COLORS = {
  critical: { bg: "bg-red-50", text: "text-red-700", border: "border-red-200", chip: "bg-red-600" },
  warning:  { bg: "bg-amber-50", text: "text-amber-700", border: "border-amber-200", chip: "bg-amber-500" },
  info:     { bg: "bg-emerald-50", text: "text-emerald-700", border: "border-emerald-200", chip: "bg-emerald-600" },
};

const PIE_COLORS = ["#14532D", "#EA580C", "#65A30D", "#0891B2", "#7C3AED", "#DC2626", "#F59E0B", "#0EA5E9", "#84CC16", "#DB2777"];

const auth = () => ({ headers: { Authorization: `Bearer ${sessionStorage.getItem("sisaur_token")}` } });

const fmtDate = (iso) => {
  if (!iso) return "-";
  try {
    const d = new Date(iso);
    return d.toLocaleString("es-VE", { day: "2-digit", month: "2-digit", year: "numeric", hour: "2-digit", minute: "2-digit", second: "2-digit", timeZone: "America/Caracas" });
  } catch { return iso; }
};

const fmtRelative = (iso) => {
  if (!iso) return "-";
  const diff = (Date.now() - new Date(iso).getTime()) / 1000;
  if (diff < 60) return `hace ${Math.floor(diff)}s`;
  if (diff < 3600) return `hace ${Math.floor(diff / 60)} min`;
  if (diff < 86400) return `hace ${Math.floor(diff / 3600)} h`;
  return `hace ${Math.floor(diff / 86400)} d`;
};

const SeverityBadge = ({ sev }) => {
  const c = SEVERITY_COLORS[sev] || SEVERITY_COLORS.info;
  return (
    <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-bold uppercase tracking-wider ${c.bg} ${c.text} border ${c.border}`}>
      <span className={`w-1.5 h-1.5 rounded-full ${c.chip}`} />
      {sev}
    </span>
  );
};

// ============ KPI Card ============
const KPI = ({ label, value, icon: Icon, tint = "green", hint }) => {
  const tints = {
    green:  "from-green-700 to-emerald-600",
    orange: "from-orange-600 to-amber-500",
    red:    "from-red-700 to-rose-600",
    blue:   "from-cyan-700 to-sky-600",
    purple: "from-violet-700 to-purple-600",
  };
  return (
    <Card className="overflow-hidden border-0 shadow-md">
      <div className={`bg-gradient-to-br ${tints[tint]} p-4 text-white`}>
        <div className="flex items-center justify-between">
          <div>
            <p className="text-[10px] uppercase tracking-widest text-white/80">{label}</p>
            <p className="text-2xl font-extrabold mt-1" data-testid={`kpi-${label.toLowerCase().replace(/\s+/g,'-')}`}>{value}</p>
            {hint && <p className="text-[11px] text-white/70 mt-1">{hint}</p>}
          </div>
          {Icon && <Icon className="w-9 h-9 text-white/40" strokeWidth={1.5} />}
        </div>
      </div>
    </Card>
  );
};


// ============ Tab 1: Eventos ============
function EventosTab() {
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(50);
  const [loading, setLoading] = useState(false);
  const [filters, setFilters] = useState({
    q: "", desde: "", hasta: "", severity: "", event_type: "", module: "", ip_address: "", status: "",
  });
  const [detail, setDetail] = useState(null);
  const [catalog, setCatalog] = useState({ eventos: [] });

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      Object.entries(filters).forEach(([k, v]) => { if (v) params.append(k, v); });
      params.append("page", page);
      params.append("page_size", pageSize);
      const r = await axios.get(`${API}/auditoria/eventos?${params}`, auth());
      setItems(r.data.items || []);
      setTotal(r.data.total || 0);
    } catch (e) {
      toast.error("Error cargando eventos: " + (e.response?.data?.detail || e.message));
    } finally { setLoading(false); }
  }, [filters, page, pageSize]);

  useEffect(() => { load(); }, [load]);
  useEffect(() => {
    axios.get(`${API}/auditoria/catalogo`, auth()).then(r => setCatalog(r.data)).catch(() => {});
  }, []);

  const totalPages = Math.max(1, Math.ceil(total / pageSize));

  const clearFilters = () => {
    setFilters({ q: "", desde: "", hasta: "", severity: "", event_type: "", module: "", ip_address: "", status: "" });
    setPage(1);
  };

  const exportar = async (format) => {
    try {
      const params = new URLSearchParams();
      Object.entries(filters).forEach(([k, v]) => { if (v) params.append(k, v); });
      params.append("format", format);
      const r = await axios.get(`${API}/auditoria/export?${params}`, { ...auth(), responseType: "blob" });
      const url = URL.createObjectURL(r.data);
      const a = document.createElement("a");
      a.href = url; a.download = `auditoria_${Date.now()}.${format}`; a.click();
      URL.revokeObjectURL(url);
      toast.success(`Exportado ${format.toUpperCase()} · ${items.length} filas`);
    } catch (e) { toast.error("Error exportando: " + (e.response?.data?.detail || e.message)); }
  };

  const exportarPDF = async () => {
    try {
      const params = new URLSearchParams();
      if (filters.desde) params.append("desde", filters.desde);
      if (filters.hasta) params.append("hasta", filters.hasta);
      if (filters.severity) params.append("severity", filters.severity);
      if (filters.event_type) params.append("event_type", filters.event_type);
      params.append("limit", "500");
      const r = await axios.get(`${API}/auditoria/reporte-pdf?${params}`, { ...auth(), responseType: "blob" });
      const url = URL.createObjectURL(r.data);
      window.open(url, "_blank", "noopener,noreferrer");
      toast.success("Reporte PDF generado con QR de verificación");
    } catch (e) { toast.error("Error PDF: " + (e.response?.data?.detail || e.message)); }
  };

  return (
    <div className="space-y-4">
      {/* Filtros */}
      <Card className="border-green-100">
        <CardContent className="p-4 space-y-3">
          <div className="flex items-center justify-between mb-2">
            <div className="flex items-center gap-2 text-green-900">
              <Filter className="w-4 h-4" /><h3 className="font-semibold text-sm">Filtros</h3>
            </div>
            <div className="flex items-center gap-2">
              <Button size="sm" variant="outline" onClick={clearFilters} data-testid="btn-limpiar-filtros"><RefreshCw className="w-3 h-3 mr-1" />Limpiar</Button>
              <Button size="sm" variant="outline" onClick={() => exportar("csv")} data-testid="btn-export-csv"><Download className="w-3 h-3 mr-1" />CSV</Button>
              <Button size="sm" variant="outline" onClick={() => exportar("xlsx")} data-testid="btn-export-xlsx"><Download className="w-3 h-3 mr-1" />Excel</Button>
              <Button size="sm" className="bg-orange-600 hover:bg-orange-700" onClick={exportarPDF} data-testid="btn-export-pdf"><FileText className="w-3 h-3 mr-1" />Reporte PDF</Button>
            </div>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-4 gap-3">
            <div className="md:col-span-2">
              <Label className="text-xs">Búsqueda libre</Label>
              <div className="relative">
                <Search className="absolute left-2 top-2.5 w-4 h-4 text-slate-400" />
                <Input value={filters.q} onChange={e => { setFilters(f => ({ ...f, q: e.target.value })); setPage(1); }}
                  placeholder="email, nombre, ruta, ID recurso…" className="pl-8" data-testid="input-search-eventos" />
              </div>
            </div>
            <div>
              <Label className="text-xs">Desde</Label>
              <Input type="datetime-local" value={filters.desde} onChange={e => { setFilters(f => ({ ...f, desde: e.target.value })); setPage(1); }} data-testid="input-desde" />
            </div>
            <div>
              <Label className="text-xs">Hasta</Label>
              <Input type="datetime-local" value={filters.hasta} onChange={e => { setFilters(f => ({ ...f, hasta: e.target.value })); setPage(1); }} data-testid="input-hasta" />
            </div>
            <div>
              <Label className="text-xs">Severidad</Label>
              <Select value={filters.severity || "all"} onValueChange={v => { setFilters(f => ({ ...f, severity: v === "all" ? "" : v })); setPage(1); }}>
                <SelectTrigger data-testid="select-severity"><SelectValue placeholder="Todas" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Todas</SelectItem>
                  <SelectItem value="critical">🔴 Critical</SelectItem>
                  <SelectItem value="warning">🟠 Warning</SelectItem>
                  <SelectItem value="info">🟢 Info</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-xs">Tipo de evento</Label>
              <Select value={filters.event_type || "all"} onValueChange={v => { setFilters(f => ({ ...f, event_type: v === "all" ? "" : v })); setPage(1); }}>
                <SelectTrigger data-testid="select-event-type"><SelectValue placeholder="Todos" /></SelectTrigger>
                <SelectContent className="max-h-60">
                  <SelectItem value="all">Todos</SelectItem>
                  {(catalog.eventos || []).map(e => <SelectItem key={e.event_type} value={e.event_type}>{e.event_type}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-xs">IP</Label>
              <Input value={filters.ip_address} onChange={e => { setFilters(f => ({ ...f, ip_address: e.target.value })); setPage(1); }} placeholder="192.168.x.x" data-testid="input-ip" />
            </div>
            <div>
              <Label className="text-xs">Estado</Label>
              <Select value={filters.status || "all"} onValueChange={v => { setFilters(f => ({ ...f, status: v === "all" ? "" : v })); setPage(1); }}>
                <SelectTrigger data-testid="select-status"><SelectValue placeholder="Todos" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Todos</SelectItem>
                  <SelectItem value="success">Success</SelectItem>
                  <SelectItem value="failed">Failed</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Tabla */}
      <Card>
        <CardContent className="p-0">
          <div className="overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow className="bg-green-50">
                  <TableHead className="text-[11px] uppercase tracking-wider text-green-900">Fecha</TableHead>
                  <TableHead className="text-[11px] uppercase tracking-wider text-green-900">Severidad</TableHead>
                  <TableHead className="text-[11px] uppercase tracking-wider text-green-900">Evento</TableHead>
                  <TableHead className="text-[11px] uppercase tracking-wider text-green-900">Usuario</TableHead>
                  <TableHead className="text-[11px] uppercase tracking-wider text-green-900">IP</TableHead>
                  <TableHead className="text-[11px] uppercase tracking-wider text-green-900">Ruta / Recurso</TableHead>
                  <TableHead className="text-[11px] uppercase tracking-wider text-green-900">Estado</TableHead>
                  <TableHead className="text-[11px] uppercase tracking-wider text-green-900 text-right">Acciones</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {loading ? Array.from({ length: 8 }).map((_, i) => (
                  <TableRow key={i}><TableCell colSpan={8}><Skeleton className="h-6" /></TableCell></TableRow>
                )) : items.length === 0 ? (
                  <TableRow><TableCell colSpan={8} className="text-center py-10 text-slate-500">No hay eventos con los filtros actuales</TableCell></TableRow>
                ) : items.map(e => (
                  <TableRow key={e.id} className="hover:bg-green-50/50" data-testid={`row-evento-${e.id}`}>
                    <TableCell className="text-xs whitespace-nowrap"><div>{fmtDate(e.timestamp)}</div><div className="text-slate-400 text-[10px]">{fmtRelative(e.timestamp)}</div></TableCell>
                    <TableCell><SeverityBadge sev={e.severity} /></TableCell>
                    <TableCell className="text-xs font-mono">{e.event_type}</TableCell>
                    <TableCell className="text-xs"><div className="font-medium">{e.actor_email || "-"}</div><div className="text-slate-400 text-[10px]">{e.actor_role}</div></TableCell>
                    <TableCell className="text-xs font-mono">{e.ip_address || "-"}</TableCell>
                    <TableCell className="text-xs"><div className="font-mono text-[11px] truncate max-w-[220px]">{e.http_method} {e.http_path}</div>{e.resource_id && <div className="text-slate-500 text-[10px]">{e.resource_type}:{(e.resource_id || "").slice(0, 12)}…</div>}</TableCell>
                    <TableCell>{e.status === "failed" ? <Badge variant="destructive" className="text-[10px]">FAILED</Badge> : <Badge className="bg-emerald-600 text-[10px]">OK</Badge>}</TableCell>
                    <TableCell className="text-right"><Button size="sm" variant="ghost" onClick={() => setDetail(e)} data-testid={`btn-ver-${e.id}`}><Eye className="w-4 h-4" /></Button></TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
          {/* Paginación */}
          <div className="flex items-center justify-between p-3 border-t bg-green-50/40 text-xs">
            <div>Mostrando {items.length} de <b>{total.toLocaleString("es-VE")}</b> eventos · Página {page} de {totalPages}</div>
            <div className="flex items-center gap-2">
              <Select value={String(pageSize)} onValueChange={v => { setPageSize(Number(v)); setPage(1); }}>
                <SelectTrigger className="h-8 w-24" data-testid="select-page-size"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {[25, 50, 100, 200].map(n => <SelectItem key={n} value={String(n)}>{n} / pág</SelectItem>)}
                </SelectContent>
              </Select>
              <Button size="sm" variant="outline" disabled={page <= 1} onClick={() => setPage(1)}>«</Button>
              <Button size="sm" variant="outline" disabled={page <= 1} onClick={() => setPage(p => Math.max(1, p - 1))}>‹</Button>
              <Button size="sm" variant="outline" disabled={page >= totalPages} onClick={() => setPage(p => Math.min(totalPages, p + 1))}>›</Button>
              <Button size="sm" variant="outline" disabled={page >= totalPages} onClick={() => setPage(totalPages)}>»</Button>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Detalle */}
      <EventoDetailDialog evento={detail} onClose={() => setDetail(null)} />
    </div>
  );
}


function EventoDetailDialog({ evento, onClose }) {
  if (!evento) return null;
  const jsonBlock = (obj) => (
    <pre className="bg-slate-900 text-emerald-200 text-[11px] p-3 rounded-md overflow-x-auto font-mono whitespace-pre-wrap break-all max-h-72">
      {obj ? JSON.stringify(obj, null, 2) : "(vacío)"}
    </pre>
  );
  return (
    <Dialog open={!!evento} onOpenChange={o => !o && onClose()}>
      <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto" data-testid="dialog-detalle-evento">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Info className="w-5 h-5 text-green-700" />
            Detalle del Evento
            <SeverityBadge sev={evento.severity} />
          </DialogTitle>
          <DialogDescription className="font-mono text-xs">{evento.id}</DialogDescription>
        </DialogHeader>
        <div className="grid grid-cols-2 gap-3 text-xs">
          <div><div className="text-slate-500 uppercase tracking-wider text-[10px]">Fecha (UTC)</div><div className="font-mono">{evento.timestamp}</div></div>
          <div><div className="text-slate-500 uppercase tracking-wider text-[10px]">Tipo</div><div className="font-mono font-bold">{evento.event_type}</div></div>
          <div><div className="text-slate-500 uppercase tracking-wider text-[10px]">Actor</div><div>{evento.actor_email || "-"} <span className="text-slate-400">({evento.actor_role || "sin rol"})</span></div></div>
          <div><div className="text-slate-500 uppercase tracking-wider text-[10px]">IP</div><div className="font-mono">{evento.ip_address || "-"}</div></div>
          <div><div className="text-slate-500 uppercase tracking-wider text-[10px]">Método/Ruta</div><div className="font-mono">{evento.http_method} {evento.http_path}</div></div>
          <div><div className="text-slate-500 uppercase tracking-wider text-[10px]">Estado</div><div>{evento.status} {evento.error_message && <span className="text-red-600">· {evento.error_message}</span>}</div></div>
          <div><div className="text-slate-500 uppercase tracking-wider text-[10px]">Recurso</div><div>{evento.resource_type || "-"}:{evento.resource_id || "-"}</div></div>
          <div><div className="text-slate-500 uppercase tracking-wider text-[10px]">Request ID</div><div className="font-mono truncate">{evento.request_id || "-"}</div></div>
          <div className="col-span-2"><div className="text-slate-500 uppercase tracking-wider text-[10px]">User-Agent</div><div className="text-[11px] font-mono truncate">{evento.user_agent || "-"}</div></div>
        </div>
        {evento.before !== null && evento.before !== undefined && (
          <div><h4 className="text-xs font-bold text-slate-700 uppercase tracking-wider mb-1">Antes</h4>{jsonBlock(evento.before)}</div>
        )}
        {evento.after !== null && evento.after !== undefined && (
          <div><h4 className="text-xs font-bold text-slate-700 uppercase tracking-wider mb-1">Después</h4>{jsonBlock(evento.after)}</div>
        )}
        {evento.metadata && Object.keys(evento.metadata).length > 0 && (
          <div><h4 className="text-xs font-bold text-slate-700 uppercase tracking-wider mb-1">Metadata</h4>{jsonBlock(evento.metadata)}</div>
        )}
        <div className="bg-emerald-50 border border-emerald-200 rounded-md p-3">
          <div className="text-[10px] uppercase tracking-wider text-emerald-700 font-bold mb-1">🔒 Firma criptográfica (hash-chain)</div>
          <div className="grid grid-cols-1 gap-1 font-mono text-[10px] break-all">
            <div><span className="text-slate-500">prev_hash:</span> {evento.prev_hash}</div>
            <div><span className="text-slate-500">hash:</span> <b>{evento.hash}</b></div>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}


// ============ Tab 2: Sesiones activas ============
function SesionesTab() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await axios.get(`${API}/auditoria/sesiones-activas`, auth());
      setItems(r.data.items || []);
    } catch (e) { toast.error("Error: " + (e.response?.data?.detail || e.message)); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => {
    load();
    const t = setInterval(load, 15000); // refresco cada 15s
    return () => clearInterval(t);
  }, [load]);

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2 text-green-900">
          <Users className="w-5 h-5" />
          <h3 className="font-bold">Usuarios conectados ahora</h3>
          <Badge className="bg-emerald-600" data-testid="badge-sesiones-count">{items.length}</Badge>
        </div>
        <Button size="sm" variant="outline" onClick={load} data-testid="btn-refresh-sesiones"><RefreshCw className="w-3 h-3 mr-1" />Actualizar</Button>
      </div>
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
        {loading ? Array.from({ length: 3 }).map((_, i) => <Skeleton key={i} className="h-32" />)
          : items.length === 0 ? (
            <Card className="col-span-full"><CardContent className="p-8 text-center text-slate-500">No hay sesiones activas en los últimos 30 minutos</CardContent></Card>
          ) : items.map(s => (
            <Card key={s.user_id} className="border-l-4 border-emerald-500 shadow-sm" data-testid={`sesion-${s.user_id}`}>
              <CardContent className="p-4">
                <div className="flex items-start justify-between mb-2">
                  <div className="flex items-center gap-2">
                    <div className="w-9 h-9 bg-emerald-100 text-emerald-800 rounded-full flex items-center justify-center font-bold text-sm">
                      {(s.user_nombre || s.user_email || "?").charAt(0).toUpperCase()}
                    </div>
                    <div>
                      <p className="font-semibold text-sm text-green-900">{s.user_nombre || s.user_email}</p>
                      <p className="text-[11px] text-slate-500">{s.user_email}</p>
                    </div>
                  </div>
                  <Badge variant="outline" className="text-[10px] uppercase">{s.user_role}</Badge>
                </div>
                <div className="space-y-1 text-xs text-slate-600">
                  <div className="flex items-center gap-1.5"><MapPin className="w-3 h-3" /><span className="font-mono">{s.ip_address || "-"}</span></div>
                  <div className="flex items-center gap-1.5"><Clock className="w-3 h-3" />Última actividad: <b>{fmtRelative(s.ultima_actividad)}</b></div>
                  {s.login_at && <div className="flex items-center gap-1.5"><LockKeyhole className="w-3 h-3" />Login: {fmtRelative(s.login_at)}</div>}
                  <div className="text-[10px] text-slate-400 truncate mt-1" title={s.user_agent}>{s.user_agent}</div>
                </div>
              </CardContent>
            </Card>
          ))}
      </div>
    </div>
  );
}


// ============ Tab 3: Estadísticas ============
function EstadisticasTab() {
  const [dias, setDias] = useState(30);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [integrity, setIntegrity] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [r1, r2] = await Promise.all([
        axios.get(`${API}/auditoria/estadisticas?dias=${dias}`, auth()),
        axios.get(`${API}/auditoria/verify-integrity?limit=1000`, auth()),
      ]);
      setData(r1.data);
      setIntegrity(r2.data);
    } catch (e) { toast.error("Error: " + (e.response?.data?.detail || e.message)); }
    finally { setLoading(false); }
  }, [dias]);

  useEffect(() => { load(); }, [load]);

  if (loading && !data) return <div className="space-y-4"><Skeleton className="h-32" /><Skeleton className="h-64" /></div>;
  if (!data) return null;

  const { kpis } = data;
  const eventTypeData = data.por_event_type.map(x => ({ name: x.event_type.split(".").slice(-1)[0], full: x.event_type, total: x.total }));

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2 text-green-900">
          <TrendingUp className="w-5 h-5" />
          <h3 className="font-bold">Estadísticas · últimos {dias} días</h3>
        </div>
        <div className="flex items-center gap-2">
          <Select value={String(dias)} onValueChange={v => setDias(Number(v))}>
            <SelectTrigger className="w-32" data-testid="select-dias"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="1">1 día</SelectItem>
              <SelectItem value="7">7 días</SelectItem>
              <SelectItem value="30">30 días</SelectItem>
              <SelectItem value="90">90 días</SelectItem>
              <SelectItem value="365">1 año</SelectItem>
            </SelectContent>
          </Select>
          <Button size="sm" variant="outline" onClick={load}><RefreshCw className="w-3 h-3 mr-1" />Recargar</Button>
        </div>
      </div>

      {/* KPIs */}
      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3">
        <KPI label="Total eventos" value={kpis.total_eventos.toLocaleString("es-VE")} icon={Activity} tint="green" />
        <KPI label="Críticos" value={kpis.criticos.toLocaleString("es-VE")} icon={ShieldAlert} tint="red" />
        <KPI label="Warnings" value={kpis.warnings.toLocaleString("es-VE")} icon={AlertTriangle} tint="orange" />
        <KPI label="Logins OK" value={kpis.logins_ok.toLocaleString("es-VE")} icon={ShieldCheck} tint="green" />
        <KPI label="Logins fallidos" value={kpis.logins_fail.toLocaleString("es-VE")} icon={XCircle} tint="red" />
        <KPI label="Alertas pendientes" value={kpis.alertas_pendientes.toLocaleString("es-VE")} icon={AlertTriangle} tint="orange" />
      </div>

      {/* Integridad */}
      {integrity && (
        <Card className={integrity.ok ? "border-emerald-300 bg-emerald-50" : "border-red-300 bg-red-50"}>
          <CardContent className="p-4 flex items-center gap-3">
            {integrity.ok ? <CheckCircle2 className="w-8 h-8 text-emerald-600" /> : <XCircle className="w-8 h-8 text-red-600" />}
            <div>
              <p className={`font-bold ${integrity.ok ? "text-emerald-800" : "text-red-800"}`} data-testid="integrity-status">
                {integrity.ok ? "Cadena de integridad VÁLIDA" : `⚠ Cadena ALTERADA — ${integrity.alteraciones.length} inconsistencias`}
              </p>
              <p className="text-xs text-slate-600">{integrity.total_verificados} eventos verificados con hash-chain SHA-256</p>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Charts */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <Card>
          <CardHeader><CardTitle className="text-sm text-green-900">Actividad diaria</CardTitle></CardHeader>
          <CardContent>
            <ResponsiveContainer width="100%" height={260}>
              <LineChart data={data.serie_diaria}>
                <CartesianGrid strokeDasharray="3 3" opacity={0.3} />
                <XAxis dataKey="day" fontSize={10} />
                <YAxis fontSize={10} />
                <Tooltip />
                <Legend fontSize={10} />
                <Line type="monotone" dataKey="critical" stroke="#DC2626" strokeWidth={2} />
                <Line type="monotone" dataKey="warning" stroke="#F59E0B" strokeWidth={2} />
                <Line type="monotone" dataKey="info" stroke="#10B981" strokeWidth={2} />
              </LineChart>
            </ResponsiveContainer>
          </CardContent>
        </Card>
        <Card>
          <CardHeader><CardTitle className="text-sm text-green-900">Top eventos por tipo</CardTitle></CardHeader>
          <CardContent>
            <ResponsiveContainer width="100%" height={260}>
              <BarChart data={eventTypeData} layout="vertical">
                <CartesianGrid strokeDasharray="3 3" opacity={0.3} />
                <XAxis type="number" fontSize={10} />
                <YAxis type="category" dataKey="name" fontSize={9} width={80} />
                <Tooltip formatter={(v, n, p) => [v, p?.payload?.full || n]} />
                <Bar dataKey="total" fill="#14532D" />
              </BarChart>
            </ResponsiveContainer>
          </CardContent>
        </Card>
        <Card>
          <CardHeader><CardTitle className="text-sm text-green-900">Top 10 usuarios más activos</CardTitle></CardHeader>
          <CardContent>
            <Table>
              <TableHeader><TableRow><TableHead>Usuario</TableHead><TableHead>Rol</TableHead><TableHead className="text-right">Eventos</TableHead></TableRow></TableHeader>
              <TableBody>
                {data.top_usuarios.map(u => (
                  <TableRow key={u.user_id}>
                    <TableCell className="text-xs"><b>{u.nombre || u.email}</b><br /><span className="text-slate-400 text-[10px]">{u.email}</span></TableCell>
                    <TableCell><Badge variant="outline" className="text-[10px]">{u.role}</Badge></TableCell>
                    <TableCell className="text-right font-bold text-orange-700">{u.total.toLocaleString("es-VE")}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </CardContent>
        </Card>
        <Card>
          <CardHeader><CardTitle className="text-sm text-green-900">Top 10 IPs por actividad</CardTitle></CardHeader>
          <CardContent>
            <Table>
              <TableHeader><TableRow><TableHead>IP</TableHead><TableHead>Usuarios únicos</TableHead><TableHead className="text-right">Eventos</TableHead></TableRow></TableHeader>
              <TableBody>
                {data.top_ips.map(i => (
                  <TableRow key={i.ip}>
                    <TableCell className="text-xs font-mono">{i.ip}</TableCell>
                    <TableCell>{i.usuarios_unicos}</TableCell>
                    <TableCell className="text-right font-bold text-cyan-700">{i.total.toLocaleString("es-VE")}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}


// ============ Tab 4: Alertas ============
function AlertasTab() {
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(false);
  const [detail, setDetail] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await axios.get(`${API}/auditoria/alertas`, auth());
      setItems(r.data.items || []);
      setTotal(r.data.total_pendientes || 0);
    } catch (e) { toast.error("Error: " + (e.response?.data?.detail || e.message)); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);

  const marcarRevisada = async (id) => {
    try {
      await axios.post(`${API}/auditoria/alertas/${id}/marcar-revisada`, {}, auth());
      toast.success("Alerta marcada como revisada");
      load();
    } catch (e) { toast.error("Error: " + (e.response?.data?.detail || e.message)); }
  };

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2 text-green-900">
          <AlertTriangle className="w-5 h-5 text-orange-600" />
          <h3 className="font-bold">Alertas críticas pendientes de revisión</h3>
          <Badge className={total > 0 ? "bg-red-600 animate-pulse" : "bg-slate-500"} data-testid="badge-alertas-count">{total}</Badge>
        </div>
        <Button size="sm" variant="outline" onClick={load}><RefreshCw className="w-3 h-3 mr-1" />Actualizar</Button>
      </div>

      {loading ? <Skeleton className="h-40" /> : items.length === 0 ? (
        <Card><CardContent className="p-10 text-center">
          <ShieldCheck className="w-14 h-14 text-emerald-500 mx-auto mb-3" />
          <p className="text-emerald-800 font-bold">Sin alertas pendientes</p>
          <p className="text-xs text-slate-500 mt-1">Todos los eventos críticos han sido revisados por SuperAdmin.</p>
        </CardContent></Card>
      ) : (
        <div className="space-y-2">
          {items.map(a => (
            <Card key={a.id} className="border-l-4 border-red-500 shadow-sm" data-testid={`alerta-${a.id}`}>
              <CardContent className="p-4 flex items-start justify-between gap-3">
                <div className="flex-1">
                  <div className="flex items-center gap-2 mb-1">
                    <SeverityBadge sev={a.severity} />
                    <span className="text-xs font-mono font-bold text-red-800">{a.event_type}</span>
                    <span className="text-xs text-slate-500">{fmtRelative(a.timestamp)}</span>
                  </div>
                  <div className="text-sm">
                    <span className="text-slate-600">Actor:</span> <b>{a.actor_email || "Anónimo"}</b>
                    {" · "}<span className="text-slate-600">IP:</span> <span className="font-mono">{a.ip_address || "-"}</span>
                    {a.resource_id && <> · <span className="text-slate-600">Recurso:</span> <span className="font-mono text-[11px]">{a.resource_type}:{(a.resource_id || "").slice(0, 12)}…</span></>}
                  </div>
                  {a.error_message && <div className="text-xs text-red-700 mt-1">{a.error_message}</div>}
                </div>
                <div className="flex gap-2 flex-shrink-0">
                  <Button size="sm" variant="outline" onClick={() => setDetail(a)} data-testid={`btn-ver-alerta-${a.id}`}><Eye className="w-3 h-3 mr-1" />Ver</Button>
                  <Button size="sm" className="bg-emerald-600 hover:bg-emerald-700" onClick={() => marcarRevisada(a.id)} data-testid={`btn-revisar-${a.id}`}><CheckCircle2 className="w-3 h-3 mr-1" />Marcar revisada</Button>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}
      <EventoDetailDialog evento={detail} onClose={() => setDetail(null)} />
    </div>
  );
}



// =================== TAB: CAS STATS (salud de la hash-chain) ===================
function CasStatsTab() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [orphansLimit, setOrphansLimit] = useState(50);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await axios.get(
        `${API}/auditoria/cas-stats?include_orphans=true&orphans_limit=${orphansLimit}`,
        auth()
      );
      setData(r.data);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Error al cargar CAS stats");
    } finally { setLoading(false); }
  }, [orphansLimit]);

  useEffect(() => { load(); }, [load]);
  useEffect(() => {
    const t = setInterval(load, 15000); // Auto-refresh cada 15s
    return () => clearInterval(t);
  }, [load]);

  const attemptsChart = useMemo(() => {
    if (!data?.attempts_histogram) return [];
    return Object.entries(data.attempts_histogram)
      .map(([attempts, count]) => ({ attempts: Number(attempts), count: Number(count) }))
      .sort((a, b) => a.attempts - b.attempts);
  }, [data]);

  const salud = useMemo(() => {
    if (!data) return { color: "gray", label: "Cargando…" };
    const orphans = data.orphans_persisted_total || 0;
    const p95 = data.attempts_p95 || 0;
    if (orphans > 0) return { color: "red", label: "Contención crítica" };
    if (p95 > 10) return { color: "amber", label: "Contención moderada" };
    if (p95 > 3) return { color: "yellow", label: "Contención leve" };
    return { color: "green", label: "Saludable" };
  }, [data]);

  return (
    <div className="space-y-6" data-testid="cas-stats-tab">
      {/* Banner de salud */}
      <Card className={`p-4 border-2 ${
        salud.color === "green" ? "border-green-200 bg-green-50" :
        salud.color === "yellow" ? "border-yellow-200 bg-yellow-50" :
        salud.color === "amber" ? "border-amber-300 bg-amber-50" :
        salud.color === "red" ? "border-red-300 bg-red-50" :
        "border-slate-200 bg-slate-50"
      }`}>
        <div className="flex items-center justify-between flex-wrap gap-3">
          <div className="flex items-center gap-3">
            <div className={`w-12 h-12 rounded-2xl flex items-center justify-center ${
              salud.color === "green" ? "bg-green-100" :
              salud.color === "red" ? "bg-red-100" : "bg-amber-100"
            }`}>
              {salud.color === "green"
                ? <ShieldCheck className="w-6 h-6 text-green-700" />
                : <ShieldAlert className={`w-6 h-6 ${salud.color === "red" ? "text-red-700" : "text-amber-700"}`} />}
            </div>
            <div>
              <div className="font-display font-bold text-lg" data-testid="cas-salud-label">Salud del CAS: {salud.label}</div>
              <div className="text-xs text-slate-600">
                Monitor de contención de la hash-chain del audit-log. Auto-refresh cada 15s.
              </div>
            </div>
          </div>
          <Button variant="outline" onClick={load} disabled={loading} data-testid="btn-cas-refresh">
            <RefreshCw className={`w-4 h-4 mr-2 ${loading ? "animate-spin" : ""}`} /> Refrescar
          </Button>
        </div>
      </Card>

      {/* KPI Cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <Card className="p-4">
          <div className="text-xs text-slate-500">Wins totales</div>
          <div className="text-2xl font-bold text-emerald-700" data-testid="cas-total-wins">{data?.total_wins ?? 0}</div>
          <div className="text-[11px] text-slate-500 mt-1">audit-log entries en hash-chain</div>
        </Card>
        <Card className="p-4">
          <div className="text-xs text-slate-500">Intentos p95</div>
          <div className={`text-2xl font-bold ${(data?.attempts_p95 || 0) > 10 ? "text-amber-700" : "text-emerald-700"}`} data-testid="cas-p95">
            {data?.attempts_p95 ?? 0}
          </div>
          <div className="text-[11px] text-slate-500 mt-1">95% wins en ≤ N intentos</div>
        </Card>
        <Card className="p-4">
          <div className="text-xs text-slate-500">Intentos p99</div>
          <div className={`text-2xl font-bold ${(data?.attempts_p99 || 0) > 20 ? "text-red-700" : "text-emerald-700"}`} data-testid="cas-p99">
            {data?.attempts_p99 ?? 0}
          </div>
          <div className="text-[11px] text-slate-500 mt-1">99% wins en ≤ N intentos</div>
        </Card>
        <Card className="p-4">
          <div className="text-xs text-slate-500">Orphans persistidos</div>
          <div className={`text-2xl font-bold ${(data?.orphans_persisted_total || 0) > 0 ? "text-red-700" : "text-emerald-700"}`} data-testid="cas-orphans-total">
            {data?.orphans_persisted_total ?? 0}
          </div>
          <div className="text-[11px] text-slate-500 mt-1">en dead-letter queue</div>
        </Card>
      </div>

      {/* Config actual */}
      <Card className="p-4">
        <div className="font-semibold mb-2 flex items-center gap-2"><Info className="w-4 h-4 text-slate-500" /> Configuración actual del CAS</div>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3 text-sm">
          <div>
            <div className="text-xs text-slate-500">Max attempts</div>
            <div className="font-mono font-bold text-slate-900">{data?.max_attempts ?? "—"}</div>
          </div>
          <div>
            <div className="text-xs text-slate-500">Backoff base</div>
            <div className="font-mono font-bold text-slate-900">{data?.backoff_base_ms ?? "—"} ms</div>
          </div>
          <div>
            <div className="text-xs text-slate-500">Backoff cap</div>
            <div className="font-mono font-bold text-slate-900">{data?.backoff_cap_ms ?? "—"} ms</div>
          </div>
          <div>
            <div className="text-xs text-slate-500">Jitter máximo</div>
            <div className="font-mono font-bold text-slate-900">{data?.jitter_max_ms ?? "—"} ms</div>
          </div>
        </div>
        <div className="text-[11px] text-slate-500 mt-2 italic">
          Configurable via env: <code>AUDIT_MAX_CAS_ATTEMPTS</code>, <code>AUDIT_CAS_BACKOFF_BASE_MS</code>,{" "}
          <code>AUDIT_CAS_BACKOFF_CAP_MS</code>, <code>AUDIT_CAS_JITTER_MAX_MS</code>.
        </div>
      </Card>

      {/* Histograma de intentos */}
      <Card className="p-4">
        <div className="font-semibold mb-3 flex items-center gap-2"><TrendingUp className="w-4 h-4 text-slate-500" /> Histograma de intentos ganadores</div>
        {attemptsChart.length === 0 ? (
          <div className="p-6 text-center text-sm text-slate-500">Sin datos aún — el histograma se llena a medida que audit_log() gana el CAS.</div>
        ) : (
          <ResponsiveContainer width="100%" height={260}>
            <BarChart data={attemptsChart}>
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis dataKey="attempts" label={{ value: "Intentos hasta ganar", position: "insideBottom", offset: -5 }} />
              <YAxis label={{ value: "# wins", angle: -90, position: "insideLeft" }} />
              <Tooltip />
              <Bar dataKey="count" fill="#14532D" />
            </BarChart>
          </ResponsiveContainer>
        )}
        <div className="text-[11px] text-slate-500 mt-2">
          Idealmente casi todos los wins deberían estar en 1-2 intentos. Cola larga → contención alta.
        </div>
      </Card>

      {/* Orphans (dead-letter queue) */}
      <Card className="p-4">
        <div className="flex items-center justify-between flex-wrap gap-2 mb-3">
          <div className="font-semibold flex items-center gap-2">
            <AlertTriangle className={`w-4 h-4 ${(data?.orphans_persisted_total || 0) > 0 ? "text-red-500" : "text-slate-400"}`} />
            Dead-letter queue (orphans)
          </div>
          <div className="flex items-center gap-2 text-xs">
            <Label className="text-xs">Mostrar:</Label>
            <select
              value={orphansLimit}
              onChange={(e) => setOrphansLimit(Number(e.target.value))}
              className="border rounded px-2 py-1 text-xs"
              data-testid="select-orphans-limit"
            >
              <option value={20}>20</option>
              <option value={50}>50</option>
              <option value={100}>100</option>
              <option value={500}>500</option>
            </select>
          </div>
        </div>
        {(data?.orphans_persisted_total || 0) === 0 ? (
          <div className="p-3 rounded bg-green-50 border border-green-200 text-green-900 text-sm flex items-center gap-2" data-testid="cas-orphans-empty">
            <CheckCircle2 className="w-4 h-4" /> No hay eventos en la dead-letter queue. Todos los eventos entraron a la hash-chain principal.
          </div>
        ) : (
          <div className="overflow-x-auto max-h-96 overflow-y-auto border border-slate-200 rounded-lg">
            <table className="w-full text-sm">
              <thead className="bg-slate-100 sticky top-0 text-slate-800">
                <tr>
                  <th className="text-left p-2.5">Timestamp</th>
                  <th className="text-left p-2.5">Event Type</th>
                  <th className="text-left p-2.5">Actor</th>
                  <th className="text-left p-2.5">IP</th>
                  <th className="text-left p-2.5">Path</th>
                  <th className="text-center p-2.5">Intentos</th>
                  <th className="text-left p-2.5">Razón</th>
                </tr>
              </thead>
              <tbody className="bg-white">
                {(data?.orphans_sample || []).map((o, i) => (
                  <tr key={o.id || i} className="border-t border-slate-100" data-testid={`orphan-fila-${i}`}>
                    <td className="p-2.5 text-xs">{fmtDate(o.recorded_at || o.timestamp)}</td>
                    <td className="p-2.5 font-mono text-xs">{o.event_type}</td>
                    <td className="p-2.5 text-xs">{o.actor_email || "—"}</td>
                    <td className="p-2.5 font-mono text-xs">{o.ip_address || "—"}</td>
                    <td className="p-2.5 text-xs">
                      <span className="font-mono text-slate-600">{o.http_method}</span> {o.http_path}
                    </td>
                    <td className="p-2.5 text-center">
                      <Badge variant="outline" className="border-red-300 text-red-700">{o.cas_attempts}</Badge>
                    </td>
                    <td className="p-2.5 text-xs text-slate-600">{o.reason}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
        {data?.last_orphan_at && (
          <div className="text-[11px] text-red-700 mt-2">
            Último orphan: <b>{fmtDate(data.last_orphan_at)}</b>
          </div>
        )}
      </Card>
    </div>
  );
}


// ============ Main Page ============
export default function AuditoriaPage() {
  const { user } = useAuth();
  const [tab, setTab] = useState("eventos");
  const [alertasCount, setAlertasCount] = useState(0);

  // Poll de alertas para mostrar badge en el tab
  useEffect(() => {
    const load = async () => {
      try {
        const r = await axios.get(`${API}/auditoria/alertas?limit=1`, auth());
        setAlertasCount(r.data.total_pendientes || 0);
      } catch { /* ignore */ }
    };
    load();
    const t = setInterval(load, 30000);
    return () => clearInterval(t);
  }, []);

  if (user?.role !== "superadmin") {
    return (
      <div className="max-w-2xl mx-auto mt-16">
        <Card className="border-red-200">
          <CardContent className="p-10 text-center">
            <LockKeyhole className="w-14 h-14 text-red-500 mx-auto mb-3" />
            <p className="font-bold text-red-800">Acceso restringido</p>
            <p className="text-xs text-slate-600 mt-1">Solo el Superadministrador puede acceder al módulo de Auditoría.</p>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="auditoria-page">
      {/* Header */}
      <div className="relative overflow-hidden rounded-2xl bg-gradient-to-r from-green-900 via-emerald-800 to-lime-700 p-6 text-white shadow-xl">
        <div className="absolute inset-0 opacity-10" style={{ backgroundImage: "radial-gradient(circle at 20% 50%, white 1px, transparent 1px)", backgroundSize: "24px 24px" }} />
        <div className="relative flex items-center justify-between">
          <div className="flex items-start gap-4">
            <div className="w-14 h-14 bg-white/15 rounded-2xl flex items-center justify-center backdrop-blur-sm">
              <Shield className="w-8 h-8 text-orange-300" />
            </div>
            <div>
              <p className="text-[10px] uppercase tracking-[0.2em] text-orange-200/90">Módulo de Auditoría</p>
              <h1 className="font-display font-extrabold text-2xl md:text-3xl mt-1">Auditoría del Sistema</h1>
              <p className="text-xs text-white/70 mt-1">Trazabilidad completa · Hash-chain inmutable · Retención 365 días</p>
            </div>
          </div>
          <div className="hidden md:flex items-center gap-2">
            <Badge className="bg-orange-500 hover:bg-orange-500 text-white text-[10px]">Superadmin</Badge>
            <Badge className="bg-white/20 text-white text-[10px]"><BadgeCheck className="w-3 h-3 mr-1" />SISAUR</Badge>
          </div>
        </div>
      </div>

      {/* Tabs */}
      <Tabs value={tab} onValueChange={setTab}>
        <TabsList className="grid grid-cols-5 max-w-3xl">
          <TabsTrigger value="eventos" data-testid="tab-eventos"><Activity className="w-4 h-4 mr-1" />Eventos</TabsTrigger>
          <TabsTrigger value="sesiones" data-testid="tab-sesiones"><Users className="w-4 h-4 mr-1" />Sesiones</TabsTrigger>
          <TabsTrigger value="stats" data-testid="tab-stats"><TrendingUp className="w-4 h-4 mr-1" />Estadísticas</TabsTrigger>
          <TabsTrigger value="alertas" data-testid="tab-alertas">
            <AlertTriangle className="w-4 h-4 mr-1" />Alertas
            {alertasCount > 0 && <Badge className="ml-1 bg-red-600 animate-pulse text-[10px] h-5 px-1.5">{alertasCount}</Badge>}
          </TabsTrigger>
          <TabsTrigger value="cas-stats" data-testid="tab-cas-stats"><Server className="w-4 h-4 mr-1" />CAS Stats</TabsTrigger>
        </TabsList>
        <div className="mt-4">
          <TabsContent value="eventos"><EventosTab /></TabsContent>
          <TabsContent value="sesiones"><SesionesTab /></TabsContent>
          <TabsContent value="stats"><EstadisticasTab /></TabsContent>
          <TabsContent value="alertas"><AlertasTab /></TabsContent>
          <TabsContent value="cas-stats"><CasStatsTab /></TabsContent>
        </div>
      </Tabs>
    </div>
  );
}
